import { useEffect, useRef, useState } from "react";
import { shallowEqual, useSelector } from "react-redux";
import { RootState } from "typedefs";
import PostModel from "app/models/PostModel";

import { useActions } from "app/utils/hooks";
import { fetchPosts } from "app/actions/posts";
import { clearPost, deletePost, editPost } from "app/actions/postingForm";
import groupAndSortPosts from "app/utils/hooks/useGroupAndSortPosts";

const useWorkflowPostsModel = (templateId: string) => {
  const postsActions = useActions({
    fetchPosts,
    clearPost,
    editPost,
    deletePost,
  });

  const { self } = useSelector(
    (state: RootState) => state.user,
    shallowEqual,
  );

  const [data, setData] = useState<PostModel[]>([]);
  const [groupedPosts, setGroupedPosts] = useState<
    Record<string, PostModel[]>
  >({});
  const [pending, setPending] = useState<boolean>(false);
  const [error, setError] = useState<boolean>(false);

  const postsParams = {
    spaceTemplateId: templateId,
    orderBy: "scheduled_in",
    order: "asc",
  };

  const abortControllerRef = useRef<AbortController | null>(null);

  const fetchPage = async () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();
    try {
      setError(false);
      setPending(true);
      setGroupedPosts(null);
      const res = await postsActions.fetchPosts(
        postsParams,
        abortControllerRef.current.signal,
      );
      if (!abortControllerRef.current.signal.aborted) {
        if (res) {
          if (Array.isArray(res)) {
            setData((prevData) => [...prevData, ...res]);
            setGroupedPosts((prevData) => groupAndSortPosts(res, prevData));
          } else {
            setData(res);
          }
        }
        setPending(false);
      }
    } catch (err) {
      if (!abortControllerRef.current.signal.aborted) {
        setError(true);
        setPending(false);
        console.error("Error fetching posts:", err);
      }
    }
  };

  const initialLoadPosts = () => {
    setData([]);
    setGroupedPosts(null);
    fetchPage();
  };

  useEffect(() => {
    if (templateId) {
      initialLoadPosts();
    }
    return () => {
      postsActions.clearPost();
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      setData([]);
      setGroupedPosts(null);
    };
  }, [templateId]);

  const onPostPin = () => {
    initialLoadPosts();
  };

  const onPostDelete = (post: PostModel) => {
    const confirmation = window.confirm(
      "Are you sure you want to delete this post?",
    );
    if (confirmation) {
      postsActions
        .deletePost(post.id)
        .then(() => {
          const filteredData = data.filter(
            (filteredPost: PostModel) => filteredPost.id !== post.id,
          );
          setData(filteredData);
          setGroupedPosts(groupAndSortPosts(filteredData, null));
        })
        .catch((error) => {
          console.error("Error deleting post:", error);
        });
    }
  };

  const onPostUpdate = (post: PostModel) => {
    if (post) {
      postsActions.editPost(post).then(() => {
        fetchPage();
      });
    }
  };

  const onPost = async (post: PostModel) => {
    if (!post) {
      return;
    }

    setData([{ ...post, userAvatar: self?.avatar }, ...data]);
    setGroupedPosts(groupAndSortPosts([post], groupedPosts));
  };

  return {
    data,
    groupedPosts,
    pending,
    error,
    onPostDelete,
    onPostUpdate,
    onPostPin,
    onPost,
  };
};

export default useWorkflowPostsModel;
